import { Spinner } from "@/components/ui/spinner";
import { useEffect, useContext } from "react";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import LeadCategoriesChart from "../../../components/analytics/LeadCategoriesChart";

type Props = {
  startDate: string;
  endDate: string;
  merchantId: string;
  moveInDateRange?: {
    minDays: number;
    maxDays: number;
  };
};

const AnalyticsLeadCategoriesContainer = ({
  startDate,
  endDate,
  merchantId,
  moveInDateRange,
}: Props) => {
  const { leadCategoryDefinitions } = useContext(MerchantContext);

  // Fetch lead categories data using tRPC
  const {
    data: leadCategoriesData,
    isLoading,
    error,
    refetch,
  } = trpc.analytics.leadCategories.useQuery(
    {
      merchantId,
      startDate,
      endDate,
      daysUntilMoveIn: moveInDateRange
        ? {
            min: moveInDateRange.minDays,
            max: moveInDateRange.maxDays,
          }
        : undefined,
    },
    {
      enabled: !!merchantId && !!startDate && !!endDate,
      refetchOnWindowFocus: false,
    }
  );

  // Calculate total leads and format data for display
  const displayData = (() => {
    if (
      !leadCategoriesData ||
      !Array.isArray(leadCategoriesData) ||
      leadCategoriesData.length === 0
    ) {
      // TODO: Add debugging info for unexpected data structure
      if (leadCategoriesData && !Array.isArray(leadCategoriesData)) {
        console.error(
          "leadCategoriesData is not an array:",
          typeof leadCategoriesData,
          leadCategoriesData
        );
      }
      return {
        totalLeads: 0,
        categories: [],
      };
    }

    const totalLeads = leadCategoriesData[0]?.totalCount
      ? parseInt(leadCategoriesData[0].totalCount)
      : 0;

    const categories = leadCategoryDefinitions.map((categoryDef) => {
      const matchingData = leadCategoriesData.find(
        (data) => data.leadCategory === categoryDef.label
      );

      return {
        label: categoryDef.label,
        count: matchingData ? parseInt(matchingData.count) : 0,
        percentage: matchingData ? matchingData.percentage : 0,
      };
    });

    // Add "Sin Perfilar" category if it exists in the data
    const sinPerfilarData = leadCategoriesData.find((data) => data.leadCategory === "Sin Perfilar");
    const sinPerfilarCategory = sinPerfilarData
      ? {
          label: "Sin Perfilar",
          count: parseInt(sinPerfilarData.count),
          percentage: sinPerfilarData.percentage,
        }
      : null;

    // Calculate "No Cliente" category (leads that don't fit into any category)
    const categorizedCount =
      categories.reduce((sum, cat) => sum + cat.count, 0) +
      (sinPerfilarCategory ? sinPerfilarCategory.count : 0);
    const noClienteCount = totalLeads - categorizedCount;
    const noClientePercentage =
      totalLeads > 0 ? parseFloat(((noClienteCount / totalLeads) * 100).toFixed(1)) : 0;

    // Add "Sin Perfilar" and "No Cliente" categories to the list
    const allCategories = [
      ...categories,
      ...(sinPerfilarCategory ? [sinPerfilarCategory] : []),
      {
        label: "No Cliente",
        count: noClienteCount,
        percentage: noClientePercentage,
      },
    ];

    return {
      totalLeads,
      categories: allCategories,
    };
  })();

  // Refetch when dependencies change
  useEffect(() => {
    if (merchantId && startDate && endDate) {
      refetch();
    }
  }, [startDate, endDate, moveInDateRange, merchantId, refetch]);

  return (
    <div className="flex grow h-full flex-col p-6">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spinner size="xl" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-full">
          <p className="text-red-500">Error al cargar datos: {error.message}</p>
        </div>
      ) : displayData.totalLeads === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-lg text-gray-500 mb-2">
              No hay datos para el periodo seleccionado
            </p>
            <p className="text-sm text-gray-400">
              Intenta seleccionar un rango de fechas diferente
            </p>
          </div>
        </div>
      ) : (
        <LeadCategoriesChart
          totalLeads={displayData.totalLeads}
          categories={displayData.categories}
        />
      )}
    </div>
  );
};

export default AnalyticsLeadCategoriesContainer;
